import { Usage } from "@hydrologiq/schemas/1.0.0/installationPage"
import Energy from "../../../figures/Energy"
import { EnergyPeriod, EnergyUnit } from "@hydrologiq/common/types/energy/consumption"
import {
  IANATimeZone,
  endOfDay,
  nowDateTime,
  previousDay,
  startOfDay,
  toTimestamp,
  formatYYYYMMDD,
} from "@hydrologiq/common/helpers/date"
import { useQuery } from "@apollo/client"
import { InstallationEnergyConsumptionDocument, InstallationWeightConsumptionDocument } from "@hydrologiq/gql/graphql"
import { DateTime, TimeZone } from "timezonecomplete"

export interface UsageFigureProps {
  title: string
  usage: Usage
  installationId: string
  installationEndDate: DateTime
  directComparison?: boolean
}

function UsageFigure({ title, usage, installationId, installationEndDate, directComparison }: UsageFigureProps) {
  const dateNow = nowDateTime()

  const endOfInstallation = dateNow.unixUtcMillis() > installationEndDate.unixUtcMillis()
  const endDate = endOfInstallation ? installationEndDate : dateNow
  const prevDate = previousDay(endDate, endOfInstallation)
  const dayBefore = previousDay(prevDate, endOfInstallation)

  const { loading, error, data } = useQuery(
    usage.field === "weight" ? InstallationWeightConsumptionDocument : InstallationEnergyConsumptionDocument,
    {
      variables: {
        id: installationId,
        start: toTimestamp(startOfDay(dayBefore), true),
        stop: toTimestamp(endOfDay(prevDate), true),
        location: IANATimeZone(),
      },
    }
  )
  let previousDayValue =
    data && data.values && data.values.length > 1
      ? data.values[1]?.value
      : data?.values?.length == 1 && data.values[0]?.value
  const dayBeforeValue = data && data.values && data.values.length > 1 && data.values[0] && data.values[0].value

  // If the previous day time is today then we set true
  const today =
    !dayBeforeValue ||
    (previousDayValue &&
      data?.values?.[1]?.time &&
      formatYYYYMMDD(new DateTime(data.values[1].time)) === formatYYYYMMDD(endDate.convert(TimeZone.utc())))

  return (
    <Energy
      heading={title}
      desiredUnit={usage.desiredUnit as EnergyUnit}
      quantity={previousDayValue ? (previousDayValue as number) : 0}
      period={today ? "today" : (usage.period as EnergyPeriod)}
      previousQuantity={dayBeforeValue ? (dayBeforeValue as number) : 0}
      dp={2}
      sourceUnit={usage.sourceUnit && (usage.sourceUnit as EnergyUnit)}
      loading={loading}
      error={error ? error.message : undefined}
      comparisonDirect={directComparison}
    />
  )
}

export default UsageFigure
